import type { MaybeRefOrGetter } from 'vue';
import { useListSchoolCampusesLazyQuery } from '@/types/vue-apollo-helpers';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import type { CampusFilterInput } from '@/types/composition-functions';

export const useCampuses = ({
  autoFetch = false,
  filter,
}: {
  autoFetch?: boolean;
  onResultHandler?: () => void;
  filter?: MaybeRefOrGetter<CampusFilterInput | undefined>;
} = {}) => {
  const { isTertiary } = useSchoolFeatures();

  const { result: campusResult,
    loading: loadingCampuses,
    refetch: refetchCampuses,
    load: loadCampusesOriginal,
    error: errorCampuses,
    onResult: onResultCampuses,
  } = useListSchoolCampusesLazyQuery(
    () => ({ filter: toValue(filter) }),
    // A search keeps the current rows on screen until the filtered page arrives,
    // and a campus created elsewhere shows up without a reload.
    { fetchPolicy: 'cache-and-network', keepPreviousResult: true },
  );

  const campuses = computed(() => campusResult.value?.listSchoolCampuses?.data || []);

  const loadCampuses = async() => {
    if (isTertiary.value) {
      await loadCampusesOriginal();
    }
  };

  if (autoFetch) {
    watch(isTertiary, (newValue) => {
      if (newValue) {
        loadCampusesOriginal();
      }
    }, { immediate: true });
  }

  return {
    campuses,
    loadingCampuses,
    loadCampuses,
    refetchCampuses,
    errorCampuses,
    onResultCampuses,
  };
};
